import { useMemo } from "react";

import { Order, User, UserSearch } from "@web-app/types";

import { useGetDrivers } from "./useDriver";

/**
 * Convert UserSearch to User type
 * UserSearch has driverProfile, User has driverDetails
 */
const userSearchToUser = (userSearch: UserSearch): User => {
  return {
    ...userSearch,
    driverDetails: userSearch.driverProfile,
  } as User;
};

/**
 * Hook to batch fetch assignees for a list of orders
 * Extracts unique assigneeIds from orders and fetches them in a single request
 */
export const useOrderAssignees = (orders: Order[]) => {
  // Create a stable key based on order IDs and assigneeIds to trigger recalculation
  // when assignments change
  const ordersKey = useMemo(
    () =>
      orders
        .map((o) => `${o.id}:${o.assigneeId || ""}`)
        .sort()
        .join(","),
    [orders],
  );

  const assigneeIds = useMemo(() => {
    const ids = new Set<string>();
    orders.forEach((order) => {
      if (order.assigneeId) {
        ids.add(order.assigneeId);
      }
    });
    return Array.from(ids);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordersKey]);

  const { data: drivers, isLoading, isError } = useGetDrivers(assigneeIds);

  const assigneesMap = useMemo(() => {
    const map = new Map<string, User>();
    if (drivers) {
      drivers.forEach((driver) => {
        map.set(driver.id, userSearchToUser(driver));
      });
    }
    return map;
  }, [drivers]);

  return { assigneesMap, isError, isLoading };
};
